import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ChatbotWidget from '../../components/ui/ChatbotWidget';
import SearchFilters from './components/SearchFilters';
import SortingControls from './components/SortingControls';
import InstitutionCard from './components/InstitutionCard';
import InstitutionModal from './components/InstitutionModal';
import ComparisonTool from './components/ComparisonTool';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// ── Utilitaire : distance de Levenshtein (tolérance aux fautes de frappe) ──────
const getLevenshteinDistance = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      matrix[i][j] = b.charAt(i - 1) === a.charAt(j - 1)
        ? matrix[i - 1][j - 1]
        : Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
    }
  }
  return matrix[b.length][a.length];
};

// ── Images par catégorie (fallback si pas de logo en DB) ──────────────────────
const CATEGORY_IMAGES = {
  'Commerce & Gestion':        'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80',
  'Ingénierie':                'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
  'Santé':                     'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
  'Classes Prépa (CPGE)':      'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80',
  'Classes Prépa (Excellence)':'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=800&q=80',
  'Sciences & Techniques':     'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=800&q=80',
  'Technologie (DUT)':         'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80',
};
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=80';

/**
 * Mappe un enregistrement schools (Supabase) vers le format attendu par les
 * composants UI (InstitutionCard, InstitutionModal, ComparisonTool…).
 */
const mapSchoolData = (s) => {
  // Type interne utilisé pour les couleurs/labels des cartes
  let mappedType = 'universite-publique';
  if (s.category?.includes('Ingénierie'))  mappedType = 'ecole-ingenieur';
  else if (s.category?.includes('Commerce')) mappedType = 'ecole-commerce';
  else if (s.type === 'Privé')              mappedType = 'universite-privee';

  // Programmes : extrait les noms depuis le JSONB ou utilise la catégorie
  const programNames = Array.isArray(s.programs) && s.programs.length > 0
    ? s.programs.map(p => (typeof p === 'string' ? p : p.name)).filter(Boolean)
    : [s.category];

  return {
    ...s,
    originalType: s.type,
    type: mappedType,
    image: s.logo_url || CATEGORY_IMAGES[s.category] || FALLBACK_IMAGE,
    previousThreshold: s.admission_threshold || 0,
    fees: s.annual_fees ?? 0,
    hasScholarships: s.has_scholarships ?? true,
    hasInternships: s.has_internships ?? true,
    hasExchange: s.has_international_exchange ?? false,
    isNew: false,
    programs: programNames,
    acceptedBacTypes: s.accepted_bac_types?.length
      ? s.accepted_bac_types
      : ['Sciences Mathématiques', 'Sciences Physiques'],
    admissionDifficulty:
      (s.admission_threshold || 0) > 14 ? 'difficile' :
      (s.admission_threshold || 0) > 12 ? 'moyen' : 'facile',
    studentCount: s.student_count ? s.student_count.toLocaleString('fr-MA') : 'N/A',
  };
};

const PostBacDatabase = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [schools, setSchools]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dbError, setDbError]     = useState(null);

  const [filters, setFilters] = useState({
    search: searchParams?.get('search') || '',
    institutionType: [],
    city: [],
    bacType: [],
    domain: [],
    feeRange: [],
    hasScholarships: false,
    hasInternships: false,
    hasExchange: false,
  });

  const [sortBy, setSortBy]                   = useState('threshold');
  const [sortOrder, setSortOrder]             = useState('desc');
  const [viewMode, setViewMode]               = useState('grid');
  const [favorites, setFavorites]             = useState([]);
  const [comparisonList, setComparisonList]   = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isModalOpen, setIsModalOpen]         = useState(false);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // ── Chargement depuis Supabase ────────────────────────────────────────────
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);
        setDbError(null);

        const { data, error } = await supabase
          .from('schools')
          .select('*')
          .eq('is_active', true)
          .order('name', { ascending: true });

        if (error) throw error;

        setSchools((data || []).map(mapSchoolData));
      } catch (error) {
        console.error('Erreur chargement écoles :', error);
        setDbError(error.message);
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  // ── Sync filtre recherche ↔ URL ───────────────────────────────────────────
  useEffect(() => {
    const searchQuery = searchParams?.get('search');
    if (searchQuery) setFilters(prev => ({ ...prev, search: searchQuery }));
  }, [searchParams]);

  // ── Filtrage + tri (mémorisé) ─────────────────────────────────────────────
  const filteredAndSortedInstitutions = useMemo(() => {
    const normalizeText = (text) =>
      text ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

    const isSmartMatch = (text, search) => {
      if (!text || !search) return false;
      const cleanText   = normalizeText(text);
      const cleanSearch = normalizeText(search);
      if (cleanText.includes(cleanSearch)) return true;
      if (cleanSearch.length > 3) {
        const dist = getLevenshteinDistance(cleanSearch, cleanText);
        const maxErrors = cleanSearch.length > 7 ? 3 : 2;
        if (dist <= maxErrors && dist < cleanSearch.length / 2) return true;
      }
      return false;
    };

    let filtered = schools.filter(inst => {
      // Recherche textuelle intelligente
      if (filters.search) {
        const match =
          isSmartMatch(inst.name, filters.search) ||
          isSmartMatch(inst.city, filters.search) ||
          isSmartMatch(inst.category, filters.search) ||
          isSmartMatch(inst.description, filters.search);
        if (!match) return false;
      }
      // Ville
      if (filters.city?.length > 0 &&
          !filters.city.some(c => inst.city?.toLowerCase().includes(c.toLowerCase())))
        return false;
      // Domaine
      if (filters.domain?.length > 0) {
        const cat = inst.category?.toLowerCase() || '';
        const domainMatch = filters.domain.some(d => {
          switch (d) {
            case 'ingenierie':      return cat.includes('ingénierie') || cat.includes('technique');
            case 'medecine':        return cat.includes('santé');
            case 'economie-gestion':return cat.includes('commerce') || cat.includes('gestion');
            default:                return false;
          }
        });
        if (!domainMatch) return false;
      }
      // Frais
      if (filters.feeRange?.length > 0) {
        const feeMatch = filters.feeRange.some(range => {
          if (range === 'gratuit')        return inst.fees === 0;
          if (range === '50000-100000')   return inst.fees > 50000;
          return false;
        });
        if (!feeMatch) return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'name':       cmp = a.name?.localeCompare(b.name); break;
        case 'city':       cmp = a.city?.localeCompare(b.city); break;
        case 'fees':       cmp = a.fees - b.fees; break;
        case 'threshold':  cmp = (b.previousThreshold || 0) - (a.previousThreshold || 0); break;
        case 'difficulty': {
          const order = { facile: 1, moyen: 2, difficile: 3 };
          cmp = order[a.admissionDifficulty] - order[b.admissionDifficulty];
          break;
        }
        default: cmp = a.name?.localeCompare(b.name);
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [filters, sortBy, sortOrder, schools]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    if (newFilters.search) setSearchParams({ search: newFilters.search });
    else setSearchParams({});
  };

  const handleClearFilters = () => {
    setFilters({
      search: '', institutionType: [], city: [], bacType: [],
      domain: [], feeRange: [], hasScholarships: false,
      hasInternships: false, hasExchange: false,
    });
    setSearchParams({});
  };

  const handleSortChange   = (s, o)     => { setSortBy(s); setSortOrder(o); };
  const handleToggleFavorite = (id)     => setFavorites(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleAddToComparison = (inst)  => {
    if (comparisonList.length < 3 && !comparisonList.find(i => i.id === inst.id))
      setComparisonList(prev => [...prev, inst]);
  };
  const handleRemoveFromComparison = (id) =>
    setComparisonList(prev => prev.filter(i => i.id !== id));
  const handleClearComparison = () => setComparisonList([]);
  const handleViewDetails  = (inst)     => { setSelectedInstitution(inst); setIsModalOpen(true); };
  const handleCloseModal   = ()         => { setIsModalOpen(false); setSelectedInstitution(null); };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <BreadcrumbNavigation />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Base de données des établissements
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Explorez les meilleures écoles supérieures (ENCG, ENSA, FMP, CPGE…).
            Données officielles et seuils d'admission.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <SearchFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            resultCount={filteredAndSortedInstitutions.length}
            isMobile={true}
            isOpen={isMobileFiltersOpen}
            onToggle={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
          />

          <div className="flex-1">
            <SortingControls
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              resultCount={filteredAndSortedInstitutions.length}
            />

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              </div>
            ) : dbError ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="AlertCircle" size={40} className="text-red-400 mx-auto mb-3" />
                <p className="text-red-700 font-medium">Impossible de charger les établissements</p>
                <p className="text-sm text-red-500 mt-1">{dbError}</p>
              </div>
            ) : filteredAndSortedInstitutions.length > 0 ? (
              <div className={`grid gap-6 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                  : 'grid-cols-1'
              }`}>
                {filteredAndSortedInstitutions.map(institution => (
                  <div key={institution.id} className="relative animate-fade-in">
                    <InstitutionCard
                      institution={institution}
                      onViewDetails={handleViewDetails}
                      onToggleFavorite={handleToggleFavorite}
                      isFavorite={favorites.includes(institution.id)}
                    />
                    <div className="absolute top-4 right-12">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddToComparison(institution)}
                        disabled={
                          comparisonList.length >= 3 ||
                          !!comparisonList.find(i => i.id === institution.id)
                        }
                        iconName="GitCompare"
                        title="Comparer"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Aucun établissement trouvé
                  {filters.search ? ` pour "${filters.search}"` : ''}
                </h3>
                <p className="text-muted-foreground mb-4">
                  Essayez de chercher par ville (ex : Rabat) ou par nom (ex : ENCG).
                </p>
                <Button onClick={handleClearFilters} variant="outline">
                  Afficher tout
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <InstitutionModal
        institution={selectedInstitution}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onToggleFavorite={handleToggleFavorite}
        isFavorite={selectedInstitution ? favorites.includes(selectedInstitution.id) : false}
      />

      <ComparisonTool
        institutions={comparisonList}
        onRemove={handleRemoveFromComparison}
        onClear={handleClearComparison}
        isOpen={isComparisonOpen}
        onToggle={() => setIsComparisonOpen(!isComparisonOpen)}
      />

      <ChatbotWidget />
    </div>
  );
};

export default PostBacDatabase;
